import React, { useState } from 'react';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import IconButton from '@material-ui/core/IconButton';
import TableContainer from '@material-ui/core/TableContainer';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import Chip from '@material-ui/core/Chip';
import Fade from '@material-ui/core/Fade';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/styles';
import { MinusIcon, PlusIcon } from '../Icons/CartIcons';
import { getUserRole } from '../../reducks/users/selectors';
import { useSelector } from 'react-redux';
import { PERMISSIONS } from '../../constants/permissions';

const useStyles = makeStyles((theme) => ({
  iconCell: {
    height: 48,
    padding: 0,
    width: 48,
  },
  price: {
    color: theme.palette.warning.main,
  },
  quantityContents: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quantityButton: {
    width: 24,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  'quantityButton svg': {
    color: '#c2773e',
  },
  'quantityButton:hover svg': {
    color: '#617d98',
  },
  quantity: {
    textAlign: 'center',
    fontSize: '1.25rem',
    lineHeight: '1px',
    marginLeft: '6px',
    marginRight: '6px',
  },
  outOfStockRow: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    position: 'relative',
  },
  outOfStockText: {
    textDecoration: 'line-through',
    color: 'rgba(0, 0, 0, 0.5)',
  },
  outOfStockChip: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    backgroundColor: '#f44336',
    color: 'white',
    fontSize: '12px',
    padding: '0px 2px',
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  outOfStockCell: {
    position: 'relative',
  },
}));

const SizeTable = (props) => {
  const classes = useStyles();
  const sizes = props.sizes;
  const userRole = useSelector(getUserRole);
  const [quantity, setQuantity] = useState(sizes.map(() => 0));
  // カート追加の書き込み中の行index(連打による二重追加防止用)
  const [addingIndex, setAddingIndex] = useState(null);
  const isViewer = userRole === PERMISSIONS.VIEWER;

  // 品切れ状態かどうかをチェックする関数
  const isOutOfStock = (item) => {
    return item.outOfStock === true;
  };

  const handleIncrement = (index) => {
    if (isOutOfStock(sizes[index])) return;

    const newQuantities = [...quantity];
    newQuantities[index] += 1;
    setQuantity(newQuantities);
  };

  const handleDecrement = (index) => {
    if (isOutOfStock(sizes[index])) return;

    const newQuantities = [...quantity];
    if (newQuantities[index] > 0) {
      newQuantities[index] -= 1;
    }
    setQuantity(newQuantities);
  };

  const handleAddProduct = async (item, index) => {
    if (isOutOfStock(item) || quantity[index] === 0) return;
    // カートへの書き込みが完了するまで再クリックを受け付けない(二重追加防止)
    if (addingIndex !== null) return;

    const newItem = {
      ...item,
      quantity: quantity[index],
    };
    setQuantity((prevQuantities) => {
      const newQuantities = [...prevQuantities];
      newQuantities[index] = 0;
      return newQuantities;
    });
    setAddingIndex(index);
    try {
      await props.addProduct(newItem);
    } finally {
      setAddingIndex(null);
    }
  };

  const handleAddFavorite = (item, index) => {
    if (isOutOfStock(item)) return;

    if (quantity[index] === 0) {
      const newQuantities = [...quantity];
      newQuantities[index] = 1;
      setQuantity(newQuantities);
    }

    const newItem = {
      ...item,
      quantity: quantity[index] || 1,
    };
    const newQuantities = [...quantity];
    newQuantities[index] = 0;
    setQuantity(newQuantities);
    props.addFavorite(newItem);
  };

  return (
    <TableContainer>
      <Table>
        <TableBody>
          {sizes.length > 0 &&
            sizes.map((item, index) => (
              <TableRow
                key={index}
                className={isOutOfStock(item) ? classes.outOfStockRow : ''}
              >
                <TableCell
                  component="th"
                  scope="row"
                  className={isOutOfStock(item) ? classes.outOfStockText : ''}
                >
                  {item.size}
                  {isOutOfStock(item) && (
                    <Fade in={true} timeout={500} style={{ marginTop: '-5px' }}>
                      <Chip
                        label="品切れ"
                        size="small"
                        className={classes.outOfStockChip}
                      />
                    </Fade>
                  )}
                </TableCell>
                <TableCell
                  className={`${classes.price} ${
                    isOutOfStock(item) ? classes.outOfStockText : ''
                  }`}
                >
                  ￥{Number(item.basePrice).toLocaleString()}
                </TableCell>
                {!isViewer && (
                  <>
                    <TableCell className={classes.quantity}>
                      <div className={classes.quantityContents}>
                        <button
                          className={`${classes.quantityButton} ${
                            isOutOfStock(item) ? classes.disabledButton : ''
                          }`}
                          onClick={() => handleDecrement(index)}
                          disabled={isOutOfStock(item)}
                        >
                          <MinusIcon />
                        </button>
                        <p
                          className={`${classes.quantity} ${
                            isOutOfStock(item) ? classes.outOfStockText : ''
                          }`}
                        >
                          {isOutOfStock(item) ? (
                            <div style={{ color: 'transparent' }}>-</div>
                          ) : (
                            quantity[index]
                          )}
                        </p>
                        <button
                          className={`${classes.quantityButton} ${
                            isOutOfStock(item) ? classes.disabledButton : ''
                          }`}
                          onClick={() => handleIncrement(index)}
                          disabled={isOutOfStock(item)}
                        >
                          <PlusIcon />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className={classes.iconCell}>
                      <Tooltip title={isOutOfStock(item) ? '品切れ中' : ''}>
                        <span>
                          <IconButton
                            onClick={() => handleAddProduct(item, index)}
                            disabled={isOutOfStock(item) || addingIndex !== null}
                            className={
                              isOutOfStock(item) || addingIndex !== null
                                ? classes.disabledButton
                                : ''
                            }
                          >
                            <ShoppingCartIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.iconCell}>
                      <Tooltip title={isOutOfStock(item) ? '品切れ中' : ''}>
                        <span>
                          <IconButton
                            onClick={() => handleAddFavorite(item, index)}
                            disabled={isOutOfStock(item)}
                            className={
                              isOutOfStock(item) ? classes.disabledButton : ''
                            }
                          >
                            <FavoriteBorderIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SizeTable;
